/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useState, useMemo } from "react";
import { MoreVertical } from "lucide-react";
import { useReactTable, getCoreRowModel, getSortedRowModel, createColumnHelper, flexRender, SortingState } from "@tanstack/react-table";
import { OP_CODE_GROUPS, DIALOGOPENFOR } from "../../../../globals/globals";
import { useProfileData } from "./hooks";
import { useRegionDisplay } from "../../../../pages/Administrations/AccessControls/Regions/hooks";
import { IProfile } from "../../../../store/api/admin/types";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import BackdropLoader from "../../../Shared/Loader/BackdropLoader";
import { getProfileListForReplace } from "../../../../pages/Administrations/helpers";
import { constStr } from "../../../../globals/constants";
import EditProfile from "./EditProfile";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../ui/table";
import { Button } from "../../../ui/button";
import { Icon } from "../../../Shared/Icon";
import { SortableHeader } from "../../../ui/sortable-header";
import { RadixSelectControl } from "../../../ui/radix-select";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/tooltip";

const Profiles: React.FC = () => {
  const {
    profileData,
    protectedProfiles,
    selectedProfile,
    handleClickDeleteAction,
    isCreateProfile,
    isEditProfile,
    openConfirmDialog,
    handleCancel,
    handleOnMenuItemClick,
    handleClickOnAddProfile,
    fallbackId,
    setFallbackId,
  } = useProfileData();

  const { geoRegions } = useRegionDisplay();

  const opCodeGroupsData = (item: any): Record<string, string[]> => {
    return item
      ?.filter((opCodeGroup: any) => !OP_CODE_GROUPS[opCodeGroup]?.isAdmin && OP_CODE_GROUPS[opCodeGroup] !== undefined)
      ?.sort()
      ?.reduce((acc: any, code: any) => {
        code = OP_CODE_GROUPS[code]?.description;
        const firstWord = code.split(" ")[0];
        acc[firstWord] = acc[firstWord] || [];
        acc[firstWord].push(code);
        return acc;
      }, {});
  };

  interface ApplicationOperationRow {
    resource: string;
    viewKey: string;
    manageKey?: string;
    required?: boolean;
  }

  type ApplicationOperationLevel = "none" | "view" | "manage" | "both";

  const buildApplicationOperationRows = (): ApplicationOperationRow[] => {
    const rows: ApplicationOperationRow[] = [];
    const seen = new Set<string>();

    for (const [key, config] of Object.entries(OP_CODE_GROUPS)) {
      if (config.isAdmin || seen.has(key)) {
        continue;
      }

      if (config.child && OP_CODE_GROUPS[config.child]) {
        const viewKey = config.child;
        const viewConfig = OP_CODE_GROUPS[viewKey];
        rows.push({
          resource: (viewConfig.description || config.description).replace(/^(View|Manage)\s+/, ""),
          viewKey,
          manageKey: key,
          required: !!viewConfig.required,
        });
        seen.add(key);
        seen.add(viewKey);
        continue;
      }

      if (!config.parent || !OP_CODE_GROUPS[config.parent]) {
        rows.push({
          resource: config.description.replace(/^(View|Manage)\s+/, ""),
          viewKey: key,
          required: !!config.required,
        });
        seen.add(key);
      }
    }

    return rows.sort((a, b) => a.resource.localeCompare(b.resource));
  };

  const getApplicationOperationLevel = (row: ApplicationOperationRow, opCodes: string[]): ApplicationOperationLevel => {
    const hasManage = row.manageKey && opCodes.includes(row.manageKey);
    const hasView = opCodes.includes(row.viewKey);

    if (hasManage && hasView) {
      return "both";
    }

    if (hasManage) {
      return "manage";
    }

    if (hasView) {
      return "view";
    }

    return "none";
  };

  const applicationOperationRows = useMemo(() => buildApplicationOperationRows(), []);

  const columnHelper = createColumnHelper<IProfile>();

  const columns = useMemo(() => [
    columnHelper.accessor("name", {
      header: ({ column }) => <SortableHeader column={column}>Profile</SortableHeader>,
      size: 150,
      cell: ({ getValue }) => <span className="font-medium text-foreground gap-2">{getValue()}</span>,
    }),
    columnHelper.accessor((row) => row.geoRegions?.[0]?.name ?? "", {
      id: "regions",
      header: ({ column }) => <SortableHeader column={column}>Regions</SortableHeader>,
      size: 180,
      cell: ({ row }) => {
        const profileRegions = row.original.geoRegions || [];
        const profileRegionNames = profileRegions.map((r) => r.name);
        const totalAvailableRegions = geoRegions?.length || 0;
        const allRegionsSelected =
          totalAvailableRegions > 0 && profileRegionNames.length === totalAvailableRegions;

        return (
          <div className="flex flex-col text-sm leading-snug text-muted-foreground gap-2">
            {allRegionsSelected ? (
              <Badge variant="secondary" className="w-fit font-normal text-xs">All regions ({totalAvailableRegions})</Badge>
            ) : (
              profileRegions.map((region) => (
                <span key={row.original.id + "." + region?.name} className="text-sm">
                  {region?.name}
                </span>
              ))
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.opCodeGroups?.filter((opCodeGroup) => OP_CODE_GROUPS[opCodeGroup]?.isAdmin).length ?? 0, {
      id: "adminOperations",
      header: ({ column }) => <SortableHeader column={column}>Admin Operations</SortableHeader>,
      size: 200,
      cell: ({ row }) => (
        <div className="flex flex-col text-sm leading-snug text-muted-foreground gap-2">
          {row.original.opCodeGroups?.filter((opCodeGroup) => OP_CODE_GROUPS[opCodeGroup]?.isAdmin).sort().map((opCode) => (
            <span key={"admin." + row.original.id + "." + opCode} className="text-sm">{OP_CODE_GROUPS[opCode]?.description}</span>
          ))}
        </div>
      ),
    }),
    columnHelper.accessor((row) => {
      const manageCount = applicationOperationRows.filter((opRow) => {
        const level = getApplicationOperationLevel(opRow, row.opCodeGroups || []);
        return level === "manage" || level === "both";
      }).length;
      return manageCount;
    }, {
      id: "appOperations",
      header: ({ column }) => (
        <div className="flex items-center gap-x-2">
          <SortableHeader column={column} className="max-w-xs">
            Application Operations
          </SortableHeader>
          <div className="grid grid-cols-[2rem_2rem_2.25rem] gap-x-4 items-center text-xs font-medium text-muted-foreground ml-auto">
            <div className="text-center">None</div>
            <div className="text-center">View</div>
            <div className="text-center">Manage</div>
          </div>
        </div>
      ),
      size: 400,
      cell: ({ row }) => (
        <div className="px-0 py-0">
          <div className="flex flex-col">
            {applicationOperationRows.map((opRow) => {
              const level = getApplicationOperationLevel(opRow, row.original.opCodeGroups || []);
              return (
                <div
                  key={opRow.viewKey}
                  className="grid grid-cols-[minmax(0,1fr)_2rem_2rem_2.25rem] items-center gap-x-2 py-0.5 text-sm text-foreground"
                >
                  <div className="min-w-0 text-sm leading-snug text-muted-foreground">{opRow.resource}</div>
                  <div className="flex justify-center text-xs">
                    {level === "none" ? <Icon name="check_circle" size={20} className="text-chart-3 font-bold antialiased" /> : ""}
                  </div>
                  <div className="flex justify-center text-xs">
                    {level === "view" ? <Icon name="check_circle" size={20} className="text-chart-3 font-bold antialiased" /> : ""}
                  </div>
                  <div className="flex justify-center text-xs">
                    {(level === "manage" || level === "both") ? <Icon name="check_circle" size={20} className="text-chart-3 font-bold antialiased" /> : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 45,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex w-full justify-center">
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="inline-block">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 border-0 bg-transparent rounded-full text-foreground hover:bg-accent"
                          aria-label="Profile settings"
                        >
                          <MoreVertical className="h-6 w-6 font-bold" strokeWidth={3} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          disabled={protectedProfiles.includes(item.name)}
                          onClick={() => handleOnMenuItemClick("Edit", item)}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleOnMenuItemClick("Duplicate", item)}>
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={protectedProfiles.includes(item.name)}
                          onClick={() => handleOnMenuItemClick("Delete", item)}
                          className="text-destructive">
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Profile settings</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    }),
  ], [protectedProfiles]);

  const data = useMemo(() => profileData ?? [], [profileData]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (!profileData || profileData.length <= 0) {
    return <BackdropLoader />;
  }

  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.PROFILE && selectedProfile && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleClickDeleteAction}>
          Are you sure you want to delete profile <strong>{selectedProfile?.name || "Unknown"}</strong>?
          <p className="text-sm text-foreground pt-3 pb-3">Select the replacement profile to continue</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="profile" className="text-sm font-medium mb-3">Profile</Label>
            <RadixSelectControl
              value={fallbackId ? String(fallbackId) : "placeholder"}
              options={[
                { value: "placeholder", label: "None" },
                ...getProfileListForReplace(profileData, selectedProfile.id || 0).map((p) => ({ value: String(p.value), label: p.text }))
              ]}
              onValueChange={(val) => setFallbackId(val === "placeholder" ? "" : val)}
            />
          </div>
        </ConfirmDialog>
      )}

      {((isEditProfile || isCreateProfile) && !!selectedProfile && <EditProfile profile={selectedProfile} handleCancel={handleCancel} />) || (
        
        <div className="flex flex-col w-full" style={{ marginRight: "15px" }}>
          {/* Toolbar */}
          <div className="flex items-center justify-end mb-2 px-1 mt-2 mr-2">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" variant="outline" onClick={() => handleClickOnAddProfile()}>
              <Icon name="add" size={18} />
              Add Profile
            </Button>
          </div>

          <div className="flex justify-between items-center ml-4 mb-0 mt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{`${data.length} results`}</span>
            </div>
          </div>
          {/* Table */}

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-3 pt-1">
              <div className="space-y-4">
                <div className="overflow-hidden rounded-lg border bg-card">
                  <div className="relative w-full overflow-x-auto overflow-y-hidden">
                    <Table className="table-fixed w-full">
                      <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                              <TableHead
                                key={header.id}
                                className={header.column.id == "appOperations" ? "px-4 py-2 h-10 whitespace-nowrap border-x border-b border-border" : "px-4 py-3 h-12 whitespace-nowrap"}
                                style={{ width: header.getSize() }}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </TableHead>
                            ))}
                          </TableRow>
                        ))}
                      </TableHeader>
                      <TableBody>
                        {table.getRowModel().rows.length > 0 ? (
                          table.getRowModel().rows.map((row) => (
                            <TableRow key={row.original.id} className="hover:bg-transparent">
                              {row.getVisibleCells().map((cell) => (
                                <TableCell
                                  key={cell.id}
                                  className={cell.column.id == "appOperations" ? "px-4 py-2 align-top border-x border-b border-border" : "px-4 py-3 align-top"}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                              No results found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Profiles;